import { useEffect } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Building2, Loader2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import type { CompanyData } from "./company/companyTypes";
import BuyerRecord from "./company/BuyerRecord";
import ProspectRecord from "./company/ProspectRecord";
import PlainCompanyRecord from "./company/PlainCompanyRecord";

// Clients and Capital Providers have pages of their own; a link that lands here forwards there.
const OWN_PAGE: Record<string, string> = { Client: "/clients", "Capital Providers": "/capital-providers" };

/**
 * Company pages by category. Buyers and prospects have their own records; a
 * firm with no page of its own (the COI category) opens here at
 * /companies/:id. /coi/:id is the person page.
 */
export default function CompanyDetail() {
  const [, buyer] = useRoute("/buyers/:id");
  const [, plain] = useRoute("/companies/:id");
  const [, prospect] = useRoute("/prospects/:id");
  const [, setLocation] = useLocation();
  const id = buyer?.id ?? prospect?.id ?? plain?.id ?? "";
  const back = buyer ? "/buyers" : prospect ? "/prospects" : "/contacts";

  const { data: company, isLoading, error } = useQuery<CompanyData>({
    queryKey: ["company", id],
    queryFn: async () => {
      const r = await fetch(`/api/companies/${id}`, { credentials: "include" });
      if (!r.ok) throw new Error("Company not found");
      return r.json();
    },
    enabled: !!id,
  });
  const own = company ? OWN_PAGE[company.category] : undefined;

  // Trigger relationship-domain email sync on load (server rate-limits to 1×/hr/user)
  useEffect(() => {
    if (company?.category) {
      fetch("/api/emails/sync-relationship-domains", { method: "POST", credentials: "include" }).catch(() => {});
      // Calendar engagement rides the same trigger on its own hourly clock.
      // Both are fire-and-forget: a sync failure must never block the page,
      // and the checkmark is derived at read time so it picks up whatever
      // landed on the previous run.
      fetch("/api/calendar/sync-engagement", { method: "POST", credentials: "include" }).catch(() => {});
    }
  }, [company?.category]);

  useEffect(() => {
    if (company && own) setLocation(`${own}/${company.id}`, { replace: true });
  }, [company?.id, own]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || own) {
    return (
      <AppLayout title="Loading...">
        <div className="flex items-center justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-primary-text" /></div>
      </AppLayout>
    );
  }
  if (error || !company) {
    return (
      <AppLayout title="Not Found">
        <div className="flex flex-col items-center justify-center py-16">
          <Building2 className="mb-4 h-16 w-16 text-muted-foreground/30" />
          <h2 className="mb-2 text-xl font-semibold">Company not found</h2>
          <Link href={back}><Button className="mt-4 gap-2"><ArrowLeft className="h-4 w-4" />Go back</Button></Link>
        </div>
      </AppLayout>
    );
  }
  if (company.category === "Buyer Network") return <BuyerRecord key={company.id} company={company} />;
  if (company.category === "Prospects") return <ProspectRecord key={company.id} company={company} />;
  return <PlainCompanyRecord key={company.id} company={company} />;
}
